import React, { useState, useEffect } from "react";
import {
  Modal,
  Stack,
  Text,
  Table,
  Button,
  Group,
  Alert,
  LoadingOverlay,
  Center,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconCheck,
  IconExternalLink,
  IconInfoCircle,
} from "@tabler/icons-react";
import { useResponsive } from "../../context/ResponsiveContext";
import {
  SyncPayrollRequest,
  PrepPayrollSyncEligibleStaff,
} from "../../types/rollfi";
import { openPayrollDashboard } from "../../utils/payrollUtils";

interface PayrollSyncConfirmationModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  eligibleStaff: PrepPayrollSyncEligibleStaff[];
  syncPayload: SyncPayrollRequest | null;
  onSync: (data: SyncPayrollRequest) => Promise<void>;
  hasUnapprovedEntries: boolean;
}

export default function PayrollSyncConfirmationModal({
  opened,
  onClose,
  onSuccess,
  eligibleStaff,
  syncPayload,
  onSync,
  hasUnapprovedEntries,
}: PayrollSyncConfirmationModalProps) {
  const { isMobile } = useResponsive();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);

  // Reset to confirmation screen when modal opens
  useEffect(() => {
    if (opened) {
      setShowSuccessScreen(false);
      setError(null);
    }
  }, [opened]);

  const handleConfirm = async () => {
    if (!syncPayload) return;

    try {
      setIsLoading(true);
      setError(null);

      await onSync(syncPayload);

      onSuccess?.();

      // Show success screen instead of closing
      setShowSuccessScreen(true);
    } catch (err) {
      console.error("Error syncing payroll:", err);
      setError(
        err instanceof Error ? err.message : "Failed to sync payroll data",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoToDashboard = () => {
    openPayrollDashboard();
    onClose();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  // Render success screen
  if (showSuccessScreen) {
    return (
      <Modal
        opened={opened}
        onClose={onClose}
        title="Payroll Synced Successfully"
        size="md"
        fullScreen={isMobile}
        centered
      >
        <Stack gap="lg" pt="sm">
          <Center>
            <ThemeIcon size={64} radius="50%" color="green" variant="light">
              <IconCheck size={48} />
            </ThemeIcon>
          </Center>

          <Stack gap="sm" align="center">
            <Text size="lg" fw={500} ta="center">
              Your data has been synced!
            </Text>
            <Text c="dimmed" ta="center" maw={400}>
              To review and finalize your payroll, navigate to the Payroll
              dashboard.
            </Text>
          </Stack>

          <Group mt="md" w="100%" justify="space-between">
            <Button variant="subtle" onClick={onClose}>
              Close
            </Button>
            <Button
              leftSection={<IconExternalLink size={16} />}
              onClick={handleGoToDashboard}
              color="teal"
            >
              Go to Payroll Dashboard
            </Button>
          </Group>
        </Stack>
      </Modal>
    );
  }

  // Render confirmation screen
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Confirm Payroll Sync"
      size="xl"
      fullScreen={isMobile}
      centered
    >
      <Stack gap="md" style={{ position: "relative" }}>
        <LoadingOverlay visible={isLoading} />

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error">
            {error}
          </Alert>
        )}

        {hasUnapprovedEntries && (
          <Alert
            icon={<IconAlertTriangle size={16} />}
            color="yellow"
            title="Unapproved Time Entries"
          >
            There are time entries that have not been approved yet. Please
            review and approve all time entries in the Timesheet tab before
            syncing to ensure accurate payroll data.
          </Alert>
        )}

        <Text size="sm" c="dimmed">
          The following staff members with RollFi accounts will have their
          payroll data synced for this period:
        </Text>

        {eligibleStaff.length === 0 ? (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="yellow"
            title="No Eligible Staff"
          >
            No staff members with RollFi user IDs found. Please ensure staff
            members are set up in the payroll system first.
          </Alert>
        ) : (
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Employee</Table.Th>
                <Table.Th style={{ textAlign: "right" }}>Hourly Rate</Table.Th>
                <Table.Th style={{ textAlign: "right" }}>Hours</Table.Th>
                <Table.Th style={{ textAlign: "right" }}>
                  Service Commission
                </Table.Th>
                <Table.Th style={{ textAlign: "right" }}>
                  Product Commission
                </Table.Th>
                <Table.Th style={{ textAlign: "right" }}>Tips</Table.Th>
                <Table.Th style={{ textAlign: "right" }}>Total</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {eligibleStaff.map((staff) => (
                <Table.Tr key={staff.employeeId}>
                  <Table.Td>
                    <div>
                      <Text size="sm" fw={500}>
                        {staff.firstName} {staff.lastName}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {staff.email}
                      </Text>
                    </div>
                  </Table.Td>
                  <Table.Td style={{ textAlign: "right" }}>
                    {staff.hourlyRate !== null
                      ? formatCurrency(staff.hourlyRate)
                      : "-"}
                  </Table.Td>
                  <Table.Td style={{ textAlign: "right" }}>
                    {staff.approvedHours} hrs
                  </Table.Td>
                  <Table.Td style={{ textAlign: "right" }}>
                    {formatCurrency(staff.serviceCommissions)}
                  </Table.Td>
                  <Table.Td style={{ textAlign: "right" }}>
                    {formatCurrency(staff.productCommissions)}
                  </Table.Td>
                  <Table.Td style={{ textAlign: "right" }}>
                    {formatCurrency(staff.tips)}
                  </Table.Td>
                  <Table.Td style={{ textAlign: "right" }}>
                    <Tooltip
                      label={staff.totalEarnedBreakdown}
                      multiline
                      w={isMobile ? 300 : 500}
                      withArrow
                      position="left"
                      styles={{ tooltip: { whiteSpace: "pre-line" } }}
                    >
                      <Group
                        gap={4}
                        justify="flex-end"
                        wrap="nowrap"
                        style={{ cursor: "help" }}
                      >
                        <Text fw={600}>
                          {formatCurrency(staff.totalEarned)}
                        </Text>
                        <IconInfoCircle
                          size={14}
                          color="var(--mantine-color-dimmed)"
                        />
                      </Group>
                    </Tooltip>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            leftSection={<IconCheck size={16} />}
            onClick={handleConfirm}
            disabled={eligibleStaff.length === 0 || !syncPayload || isLoading}
            loading={isLoading}
          >
            Confirm & Sync
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
